import IconDelete from "./Icon/Delete";
import { useSortable } from "@dnd-kit/sortable";
import React, { useMemo, useState } from "react";
import GuidelinesSelect from "../GuidelineCheckbox";
import { Avatar, Button, Input, Tooltip } from "antd";

import { ICON } from "../GuidelineCheckbox";
import ICON_PEN from "../../../assets/pen.svg";
import ICON_MOVE from "../../../assets/move.svg";
import ICON_ARROW_RIGHT from "../../../assets/arrow-right.svg";

import type { TypeTagFilterItem } from ".";

export interface TypeTagGuidelineItem
  extends TypeTagFilterItem,
    Pick<TypeTagFilterItem, "guidelines"> {}

type TypeGroupProps<T = TypeTagFilterItem> = React.FC<{
  data: TypeTagGuidelineItem;
  /**
   * @param tags 用于编辑模式“名称”查重
   */
  tags?: T[];
  /**
   * @param grabbing 抓起来时的鼠标样式
   */
  grabbing?: boolean;
  /**
   * @name onDelete 删除
   */
  onDelete?(value: T): void;
  /**
   * @name onSave 编辑名称
   */
  onSave?(value: T): Promise<void>;
}> & {
  /**
   * @name Item 就是GuidelinesSelect组件
   */
  Item: typeof GuidelinesSelect;
};

/**
 * @name Group 群组
 * @description 拖拽
 */
const Group: TypeGroupProps = ({
  tags,
  data,
  onSave,
  grabbing,
  children,
  onDelete,
}) => {
  const { id, name, documents, guidelines, isDefault } = data;

  const { setNodeRef, listeners, attributes, isDragging } = useSortable({ id });

  const [newName, setNewName] = useState<string>(name);

  const [edit, setEdit] = useState(false); // 编辑模式
  const [loading, setLoading] = useState(false); // 保存状态

  const [expand, setExpand] = useState(false);

  const WARN_MSG = useMemo(() => {
    if (!edit) return;
    if (!newName) return "Name cannot be empty";
    if (newName.length > 20) return "Name cannot exceed 20 characters";
    if (tags?.find((v) => v.id !== id && v.name === newName)) {
      return "Name already exists";
    }
  }, [edit, newName, tags]);

  function onExpandChange() {
    setExpand((b) => !b);
  }

  async function onSaveName() {
    try {
      setLoading(true);
      await onSave?.({ ...data, name: newName });
      onCancel();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  function onStopPropagation(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
  }

  function onNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setNewName(e.target.value);
  }

  function onCancel() {
    setEdit(false);
    setNewName(undefined);
  }

  const DISABLE_SAVE = Boolean(WARN_MSG);

  return (
    <div
      ref={setNodeRef}
      className={`guidelines-group ${
        isDragging ? "guidelines-group-drag" : ""
      } ${expand || edit ? "" : "guidelines-group-down"}`}
    >
      {isDragging ? null : (
        <>
          <div
            {...listeners}
            {...attributes}
            className="guidelines-group-header"
            style={{ cursor: grabbing ? "grabbing" : "grab" }}
          >
            <div>
              <img src={ICON_MOVE} alt="#" />
              <Avatar.Group size="small">
                {guidelines.slice(0, 3).map((v) => (
                  <Avatar key={v.lenderId} src={ICON[v.lenderDisplayName]} />
                ))}
              </Avatar.Group>
              <div className="guidelines-group-header-name">
                {edit ? (
                  <Tooltip title={WARN_MSG} open={DISABLE_SAVE}>
                    <Input
                      size="small"
                      defaultValue={name}
                      onChange={onNameChange}
                      onPointerDown={onStopPropagation}
                    />
                  </Tooltip>
                ) : (
                  <span>{name}</span>
                )}
              </div>
              <div className="guidelines-group-header-count">
                {documents?.length}
              </div>
              <img
                draggable={false}
                src={ICON_ARROW_RIGHT}
                onClick={onExpandChange}
                onPointerDown={onStopPropagation}
                className="guidelines-group-header-arrow"
                style={{ transform: `rotate(${expand ? 90 : 0}deg)` }}
              />
            </div>
            <div
              onPointerDown={onStopPropagation}
              className="guidelines-group-header-tools"
            >
              {edit ? (
                <>
                  <Tooltip title={loading ? "Saving" : ""}>
                    <Button disabled={loading} onClick={onCancel}>
                      Cancel
                    </Button>
                  </Tooltip>
                  <Tooltip title={loading ? "Saving" : ""}>
                    <Button
                      loading={loading}
                      onClick={onSaveName}
                      disabled={loading || DISABLE_SAVE}
                      className={`guidelines-group-btn${
                        DISABLE_SAVE ? "-disabled" : ""
                      }`}
                    >
                      Save
                    </Button>
                  </Tooltip>
                </>
              ) : isDefault ? null : (
                <>
                  <img
                    src={ICON_PEN}
                    draggable={false}
                    onClick={() => setEdit((b) => !b)}
                  />
                  <IconDelete onClick={() => onDelete?.(data)} />
                </>
              )}
            </div>
          </div>
          {expand && !isDragging && children ? (
            <div className="guidelines-group-ctx">{children}</div>
          ) : null}
        </>
      )}
    </div>
  );
};

Group.Item = GuidelinesSelect;

export default Group;
